/**
 * @fileoverview As telas do quadro compartilhado fora do quadro: carregando
 * (esqueleto), a senha e as situações em que o link não abre (inválido,
 * expirado, revogado, indisponível, muitos acessos, erro). Nenhuma mostra nada
 * do quadro — nem o nome, antes da senha.
 */

import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Skeleton from "@material-ui/lab/Skeleton";
import { Lock, Eye, EyeOff, Link2Off, Clock, Ban, CloudOff, Hourglass, AlertTriangle } from "lucide-react";

import { i18n } from "../../translate/i18n";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`sharedBoard.${key}`, options);

const Brand = () => {
  const classes = useStyles();
  return <span className={classes.footerBrand}>Sttelar Talk</span>;
};

const STATUS_ICON = {
  invalid: Link2Off,
  expired: Clock,
  revoked: Ban,
  unavailable: CloudOff,
  rateLimited: Hourglass,
  error: AlertTriangle,
};

export const StatusScreen = ({ kind, onRetry }) => {
  const classes = useStyles();
  const Icon = STATUS_ICON[kind] || AlertTriangle;
  return (
    <main className={classes.center} data-share-status={kind}>
      <section className={classes.panel} role="alert">
        <span className={classes.panelIcon}>
          <Icon size={22} strokeWidth={1.75} />
        </span>
        <h1 className={classes.panelTitle}>{t(`status.${kind}.title`)}</h1>
        <p className={classes.panelText}>{t(`status.${kind}.text`)}</p>
        {onRetry && (
          <button type="button" className={classes.ghostBtn} onClick={onRetry} style={{ marginTop: 8 }}>
            {t("status.retry")}
          </button>
        )}
      </section>
      <Brand />
    </main>
  );
};

/** A senha do link. `onSubmit` devolve null (entrou) ou a chave do erro. */
export const PasswordGate = ({ onSubmit, sessionEnded }) => {
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || sending) return;
    setSending(true);
    setError(null);
    const problem = await onSubmit(password);
    // Entrou: a tela troca para o quadro e este formulário sai.
    if (problem) {
      setError(problem);
      setSending(false);
    }
  };

  return (
    <main className={classes.center} data-share-status="password">
      <section className={classes.panel}>
        <span className={classes.panelIcon}>
          <Lock size={22} strokeWidth={1.75} />
        </span>
        <h1 className={classes.panelTitle}>{t("password.title")}</h1>
        <p className={classes.panelText}>{t("password.text")}</p>
        {sessionEnded && <p className={classes.panelNote}>{t("password.sessionEnded")}</p>}
        <form className={classes.panelForm} onSubmit={submit} noValidate>
          <TextField
            className={classes.field}
            label={t("password.label")}
            type={visible ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            variant="outlined"
            fullWidth
            autoFocus
            autoComplete="current-password"
            error={Boolean(error)}
            inputProps={{ maxLength: 200, "aria-label": t("password.label") }}
            InputProps={{
              endAdornment: (
                <button
                  type="button"
                  onClick={() => setVisible((v) => !v)}
                  aria-label={visible ? t("password.hide") : t("password.show")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", display: "inline-flex", padding: 4 }}
                >
                  {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              ),
            }}
          />
          {error && <p className={classes.panelError} role="alert">{t(`password.${error}`)}</p>}
          <button type="submit" className={classes.primaryBtn} disabled={!password || sending}>
            {sending && <CircularProgress size={16} color="inherit" />}
            {t("password.submit")}
          </button>
        </form>
      </section>
      <Brand />
    </main>
  );
};

/** O esqueleto do quadro enquanto ele carrega. */
export const BoardSkeleton = () => {
  const classes = useStyles();
  return (
    <main className={classes.root} aria-busy="true">
      <span className={classes.srOnly}>{t("loading")}</span>
      <Skeleton variant="text" width={140} height={16} className={classes.skeleton} />
      <Skeleton variant="text" width={260} height={36} className={classes.skeleton} />
      <div className={classes.skeletonColumns}>
        {[4, 3, 2].map((cards, column) => (
          <div key={column} className={classes.skeletonColumn}>
            <Skeleton variant="text" width={120} height={20} className={classes.skeleton} />
            {Array.from({ length: cards }, (_, i) => (
              <Skeleton key={i} variant="rect" height={78} className={classes.skeleton} />
            ))}
          </div>
        ))}
      </div>
    </main>
  );
};
